import { hydrate } from 'react-dom';
import { createBrowserHistory } from 'history';
import { logTiming } from '@tunein/web-utils';
import { loadableReady } from '@loadable/component';
import get from 'lodash/get';
import getRoutes from '../common/routes';
import removeElementById from '../common/utils/dom/removeElementById';
import { isDevBuild, isLocalEnv } from '../common/utils/environment';
import initDesktop from '../common/utils/desktop/initDesktop';
import { initBranch } from '../common/utils/branch/branchHelper';
import { checkIsUserOptedOutAnywhere } from '../common/utils/optOut/checkUserOptOutStatus';
import isAdBlockEnabled from '../common/components/utils/isAdBlockEnabled';
import sendCategoryActionLabelEvent from '../common/utils/sendCategoryActionLabelEvent';
import web from '../common/constants/analytics/categoryActionLabel/web';
import createTuneInApiClient from './utils/createTuneInApiClient';
import createAnalytics, { initClientAnalytics } from './utils/analytics';
import configureClientStore from './store/configureClientStore';
import Root from './Root';

export default function initClient() {
  // initialize and verify desktop state on the client
  window.INITIAL_STATE.app.isDesktop = initDesktop(
    window.INITIAL_STATE.app.isDesktop,
  );

  const tuneInApiClient = createTuneInApiClient(window.INITIAL_STATE);

  window.onload = () => {
    // Using a setTimeout here so the browser may consider this `onload` callback as `completed`. By
    // pushing the callback to the event loop, this will simply allow us to measure metrics that can
    // only be measured after all `onload` callbacks are completed (e.g. measuring the loadEventEnd
    // metric, for which https://stackoverflow.com/a/11048634 was recommended).
    setTimeout(() => {
      logTiming({ tuneInApiClient });
      isAdBlockEnabled((isEnabled) => {
        sendCategoryActionLabelEvent(
          tuneInApiClient,
          web.category,
          web.actions.adblocker,
          isEnabled,
        );
      });
    }, 0);
  };

  const browserHistory = createBrowserHistory();

  const store = configureClientStore(
    window.INITIAL_STATE,
    browserHistory,
    tuneInApiClient,
  );

  delete window.INITIAL_STATE;
  removeElementById('initialStateEl');

  const state = store.getState();
  const isUserOptedOut = checkIsUserOptedOutAnywhere(state);
  const desktopOS = get(state, 'app.desktopOS');
  const analytics = createAnalytics(tuneInApiClient, store.getState);
  initClientAnalytics(isUserOptedOut, desktopOS, browserHistory.location);
  initBranch(state, browserHistory.location.pathname);

  if (isLocalEnv()) {
    window.tiDebug = {
      tuneInApiClient,
      store,
      analytics,
      browserHistory,
      toggleActionLogs() {
        const debugState = store.getState().app.debug;
        // we can mutate state here since this is for debugging purposes only
        debugState.showActionLogs = !debugState.showActionLogs;
      },
    };
  }

  const baseProps = {
    // Since @tunein/web-tuner currently needs to store the instance of the apiClient created, had
    // to find out an easy way to pass it to the WithPlayer component. Passing it down like this was
    // the easiest way.
    apiClient: tuneInApiClient,
    locale: window.L10N.locale,
    localizations: window.L10N.localizations,
  };

  let numHotReloads = 0;

  function start() {
    hydrate(
      <Root
        baseProps={baseProps}
        store={store}
        routes={getRoutes()}
        numHotReloads={numHotReloads++}
      />,
      document.getElementById('content'),
    );
  }

  if (isDevBuild()) {
    start();

    if (module.hot) {
      // See for context on react-router + HMR:
      // - https://github.com/ReactTraining/react-router/issues/2182#issuecomment-147016657
      // - https://github.com/ReactTraining/react-router/issues/2182#issuecomment-170807654
      // This will be easier when react-router v4 drops https://github.com/ReactTraining/react-router/issues/2182#issuecomment-246901302
      module.hot.accept('../common/routes', start);
    }
  } else {
    // Instruction to wait for all bundles for the page to load before rendering
    // See https://github.com/gregberge/loadable-components
    // NOTE/TODO: depending on how other libraries should load based on page (e.g. @tunein/mint or
    // @tunein/web-tuner), this application may need to be broader.
    loadableReady(start);
  }
}
